import React from "react";
import get from "lodash/get";
import omit from "lodash/omit";
import flow from "lodash/flow";
import find from "lodash/find";
import invert from "lodash/fp/invert";
import flatMap from "lodash/fp/flatMap";
import update from "immutability-helper";
import short from "short-uuid";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Icon,
  message,
  Modal,
  Collapse,
  Table,
  Popconfirm,
} from "antd";
import { DragDropContext } from "react-beautiful-dnd";

import EventsList from "./EventsList";

const uuidTranslator = short();

class ScenarioEditorFormContainer extends React.Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      data: InternalFormData.load(this.props.definition),
    };
  }

  componentDidMount() {
    const { data } = this.state;
    const form = this.formRef.current.props.form;
    const updatedData = updateDataFromForm(data, form);
    if (this.props.onDefinitionChange) {
      const updatedDefinition = this.dumpDefinition(updatedData);
      this.props.onDefinitionChange(updatedDefinition);
    }
    this.setState({ data: updatedData });
  }

  dumpDefinition = (data) => {
    return InternalFormData.dump(data);
  };

  handleDataChange = (data) => {
    this.setState({ data });
    if (this.props.onDefinitionChange) {
      const updatedDefinition = this.dumpDefinition(data);
      this.props.onDefinitionChange(updatedDefinition);
    }
  };

  render() {
    return (
      <ScenarioEditorForm
        wrappedComponentRef={this.formRef}
        meta={this.props.meta}
        data={this.state.data}
        onDataChange={this.handleDataChange}
        onMediaSelectionRequested={this.props.onMediaSelectionRequested}
      />
    );
  }
}

/**
 * Functions describing the transformations from definition to internal form
 * data and vice versa.
 */
const InternalFormData = {
  load: (definition) => {
    const eventsById = {};
    const events = [];
    get(definition, "events", []).forEach((event) => {
      // discard events without id
      if (!event.id) {
        return;
      }

      eventsById[event.id] = event;
      events.push(event.id);
    });
    const data = {
      eventsById,
      definition: {
        ...definition,
        events,
      },
    };
    return data;
  },
  dump: (data) => {
    const definition = {
      ...data.definition,
      events: data.definition.events.map((id) => data.eventsById[id]),
    };
    return definition;
  },
};

const updateDataFromForm = (data, form) => {
  let newData = update(data, {});

  const definitionValues = omit(form.getFieldsValue(), ["eventsById"]);

  newData = update(newData, { definition: { $merge: definitionValues } });

  // Update the events based on the form fields
  const oldEventsById = data.eventsById;
  const newEventsByIdFormData = form.getFieldValue("eventsById") || {};
  const newEventsById = {};
  Object.keys(oldEventsById).forEach((id) => {
    const formId = `event-${id}`;
    newEventsById[id] = {
      ...oldEventsById[id],
      ...newEventsByIdFormData[formId],
    };
  });
  newData = update(newData, { eventsById: { $set: newEventsById } });

  return newData;
};

/**
 * Form component for changing the definition.
 */
const ScenarioEditorForm = Form.create({
  /**
   * Everytime a field changes within the form propagate the changes if needed.
   */
  onFieldsChange(props, changedFields) {
    const { form } = props;
    const { data, onDataChange } = props;

    onDataChange(updateDataFromForm(data, form));
  },
})(
  class Wrapped extends React.Component {
    state = {
      addEvent: undefined,
      lastAddedEventId: undefined,
      draggableIds: {},
    };

    /**
     * Process ordering updates when dragging ended.
     *
     * The passed in drag result contains information on source and destination
     * of the drag interaction as well as the draggable item itself.
     *
     * So when a destination is known and the the item was actually dragged
     * to a different place, we update the events list with the new ordering.
     */
    handleDragEnd = (result) => {
      const { source, destination, draggableId } = result;
      const { form, data, onDataChange } = this.props;

      if (!destination) {
        return;
      }

      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      if (result.type === "event") {
        const eventId = this.getEventIdFromDraggableId(draggableId);
        const newData = update(data, {
          definition: {
            events: {
              $splice: [
                [source.index, 1],
                [destination.index, 0, eventId],
              ],
            },
          },
        });

        onDataChange(newData);
      }

      if (result.type === "question-choice") {
        const sourceEventId = result.source.droppableId.replace(
          /^event-droppable-([^.]+)\..*$/,
          "$1"
        );
        const sourceIndex = result.source.index;
        const choice = get(data, [
          "eventsById",
          sourceEventId,
          "choices",
          sourceIndex,
        ]);

        const targetEventId = result.destination.droppableId.replace(
          /^event-droppable-([^.]+)\..*$/,
          "$1"
        );
        const targetIndex = result.destination.index;

        const newData = flow(
          (data) =>
            update(data, {
              eventsById: {
                [sourceEventId]: { choices: { $splice: [[sourceIndex, 1]] } },
              },
            }),
          (data) =>
            update(data, {
              eventsById: {
                [targetEventId]: {
                  choices: (choices) =>
                    update(choices || [], {
                      $splice: [[targetIndex, 0, choice]],
                    }),
                },
              },
            })
        )(data);

        // update the form fields associated with the events
        const fieldsUpdate = {
          [`eventsById.event-${sourceEventId}`]: omit(
            get(newData, ["eventsById", sourceEventId]),
            ["id", "type"]
          ),
          [`eventsById.event-${targetEventId}`]: omit(
            get(newData, ["eventsById", targetEventId]),
            ["id", "type"]
          ),
        };
        form.setFieldsValue(fieldsUpdate);

        onDataChange(newData);
      }
    };

    handleEventIdChangeRequested = (id, newId) => {
      const { data, onDataChange } = this.props;

      if (!newId.trim()) {
        message.error("Event ids may not be empty!");
        return;
      }

      if (get(data, ["eventsById", newId])) {
        message.error("Event ids must be unique!");
        return;
      }

      const newData = update(data, {
        eventsById: (eventsById) =>
          update(eventsById, {
            $unset: [id],
            [newId]: () => ({ ...eventsById[id], id: newId }),
          }),
        definition: {
          events: {
            $apply: (events) => events.map((x) => (x === id ? newId : x)),
          },
        },
      });
      onDataChange(newData);

      const { draggableIds } = this.state;
      draggableIds[newId] = draggableIds[id];
      delete draggableIds[id];
      this.setState({ draggableIds });
    };

    handleAddMetricRequested = () => {
      const id = (window.prompt("Enter an ID for the metric") || "").trim();
      if (!id) {
        return;
      }

      const { data, onDataChange } = this.props;
      const newData = update(data, {
        definition: {
          metrics: (metrics = []) => [...metrics, { id }],
        },
      });
      onDataChange(newData);
    };
    handleRemoveMetricRequested = (index) => {
      const { data, onDataChange } = this.props;
      const newData = update(data, {
        definition: {
          metrics: { $splice: [[index, 1]] },
        },
      });
      onDataChange(newData);
    };
    handleAddObjectRequested = () => this.setState({ addObject: true });
    handleAddObjectCancelled = () => this.setState({ addObject: undefined });
    handleAddObjectSubmitted = (values) => {
      const { data, onDataChange } = this.props;
      const newData = update(data, {
        definition: {
          objects: (objects = []) => [...objects, values],
        },
      });
      onDataChange(newData);
      this.setState({ addObject: undefined });
    };
    handleRemoveObjectRequested = (objectIndex) => {
      const { data, onDataChange } = this.props;
      const newData = update(data, {
        definition: {
          objects: { $splice: [[objectIndex, 1]] },
        },
      });
      onDataChange(newData);
    };
    handleAddObjectMetricRequested = (objectIndex) => {
      const { data, onDataChange } = this.props;

      const id = (window.prompt("Enter the ID for the metric") || "").trim();
      if (!id) {
        return;
      }

      if (!find(get(data, "definition.metrics", []), { id })) {
        window.alert("You have to provide a valid metric id");
        return;
      }

      const newData = update(data, {
        definition: {
          objects: (objects = []) =>
            objects.map((object, index) =>
              index === objectIndex
                ? update(object, {
                    metrics: (metrics = {}) => ({ ...metrics, [id]: 0 }),
                  })
                : object
            ),
        },
      });
      onDataChange(newData);
    };
    handleRemoveObjectMetricRequested = (objectIndex, metricId) => {
      const { data, onDataChange } = this.props;
      const newData = update(data, {
        definition: {
          objects: (objects = []) =>
            objects.map((object, index) =>
              index === objectIndex
                ? update(object, {
                    metrics: (metrics = {}) =>
                      update(metrics, { $unset: [metricId] }),
                  })
                : object
            ),
        },
      });
      onDataChange(newData);
    };

    handleAddEventAfterRequested = (id) => this.setState({ addEvent: id });
    handleAddEventRequested = (e) => this.setState({ addEvent: true });
    handleAddEventCancelled = (e) => this.setState({ addEvent: undefined });
    handleAddEventSubmitted = (values) => {
      const { data, onDataChange } = this.props;
      const { addEvent: addEventAfter } = this.state;
      const { type } = values;

      const id = values.id || uuidTranslator.new();
      if (!id.trim()) {
        message.error("Event ids may not be empty!");
        return;
      }

      if (get(data, ["eventsById", id])) {
        message.error("Event ids must be unique!");
        return;
      }

      const events = get(data, "definition.events", []);
      const newEvents =
        addEventAfter === true
          ? [...events, id]
          : flatMap((x) => (x === addEventAfter ? [x, id] : x), events);

      const newData = update(data, {
        eventsById: { [id]: { $set: { id, type } } },
        definition: { events: { $set: newEvents } },
      });
      onDataChange(newData);
      this.setState({ addEvent: undefined, lastAddedEventId: id });
    };

    handleRemoveEventRequested = (event) => {
      const { data, onDataChange } = this.props;
      const { id } = event;
      const newData = update(data, {
        eventsById: { $unset: [id] },
        definition: {
          events: (events) => events.filter((eventId) => eventId !== id),
        },
      });
      onDataChange(newData);
    };

    handleAddQuestionChoiceRequested = (event) => {
      const { data, onDataChange } = this.props;
      const { id } = event;

      const newData = update(data, {
        eventsById: {
          [id]: {
            choices: (choices) => update(choices || [], { $push: [{}] }),
          },
        },
      });
      onDataChange(newData);
    };

    handleRemoveQuestionChoiceRequested = (event, choiceIndex) => {
      const { data, onDataChange } = this.props;
      const { id } = event;
      const newData = update(data, {
        eventsById: { [id]: { choices: { $splice: [[choiceIndex, 1]] } } },
      });
      onDataChange(newData);
    };

    handleAddQuestionNextRuleRequested = (event) => {
      const { data, onDataChange } = this.props;
      const { id } = event;

      const newData = update(data, {
        eventsById: {
          [id]: {
            next_rules: (next_rules) =>
              update(next_rules || [], { $push: [{}] }),
          },
        },
      });
      onDataChange(newData);
    };

    handleRemoveQuestionNextRuleRequested = (event, ruleIndex) => {
      const { data, onDataChange } = this.props;
      const { id } = event;
      const newData = update(data, {
        eventsById: { [id]: { next_rules: { $splice: [[ruleIndex, 1]] } } },
      });
      onDataChange(newData);
    };

    handleAddQuestionMetricRequested = (event, choiceIndex) => {
      const { data, onDataChange } = this.props;
      const { id } = event;

      const metricId = (
        window.prompt("Enter the ID for the metric") || ""
      ).trim();
      if (!metricId) {
        return;
      }

      if (!find(get(data, "definition.metrics", []), { id: metricId })) {
        window.alert("You have to provide a valid metric id");
        return;
      }

      const newData = update(data, {
        eventsById: {
          [id]: {
            choices: (choices) => {
              if (!choices || !choices.length) {
                return choices;
              }

              return choices.map((choice, index) => {
                if (index !== choiceIndex) {
                  return choice;
                }

                return update(choice, {
                  metrics: (metrics = {}) => ({ ...metrics, [metricId]: 0 }),
                });
              });
            },
          },
        },
      });
      onDataChange(newData);
    };
    handleRemoveQuestionMetricRequested = (event, choiceIndex, metricId) => {
      const { data, onDataChange } = this.props;
      const { id } = event;
      const newData = update(data, {
        eventsById: {
          [id]: {
            choices: (choices) =>
              choices && choices.length
                ? choices.map((choice, index) =>
                    index === choiceIndex
                      ? update(choice, { metrics: { $unset: [metricId] } })
                      : choice
                  )
                : choices,
          },
        },
      });
      onDataChange(newData);
    };

    getDraggableId = (event) => {
      const { draggableIds } = this.state;
      if (!draggableIds[event.id]) {
        draggableIds[event.id] = uuidTranslator.new();
        this.setState({ draggableIds });
      }
      return draggableIds[event.id];
    };

    getEventIdFromDraggableId = (draggableId) => {
      const { draggableIds } = this.state;
      return get(invert(draggableIds), draggableId);
    };

    render() {
      const {
        form,
        meta,
        data: { eventsById, definition },
        onMediaSelectionRequested,
      } = this.props;
      const { addEvent, lastAddedEventId, addObject } = this.state;

      return (
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <Form labelCol={{ span: 3 }} wrapperCol={{ span: 21 }}>
            <Form.Item label="Language">
              {form.getFieldDecorator("locale", {
                rules: [{ required: true, message: "Select a language" }],
                validateTrigger: ["onBlur"],
                initialValue: definition.locale,
              })(
                <Select placeholder="Select a language">
                  <Select.Option value={null}>---</Select.Option>
                  {meta.locale.choices.map(({ id, name }) => (
                    <Select.Option key={id} value={id}>
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>

            <Form.Item label="Custom CSS">
              {form.getFieldDecorator("custom_css", {
                rules: [{ type: "url", message: "Enter a valid css url" }],
                validateTrigger: ["onBlur"],
                initialValue: definition.custom_css,
              })(<Input placeholder="Enter a custom CSS url (if needed)" />)}
            </Form.Item>

            <Form.Item label="Screen as Player">
              {form.getFieldDecorator("client.screen_as_player", {
                validateTrigger: ["onBlur"],
                valuePropName: "checked",
                initialValue: !!get(definition, "client.screen_as_player"),
              })(<Input type="checkbox" />)}
            </Form.Item>

            <Form.Item label="Auto next">
              {form.getFieldDecorator(
                "auto_next_on_online_players_made_choice",
                {
                  validateTrigger: ["onBlur"],
                  valuePropName: "checked",
                  initialValue: !!definition.auto_next_on_online_players_made_choice,
                }
              )(<Input type="checkbox" />)}
            </Form.Item>

            <Form.Item label="Livestream">
              {form.getFieldDecorator("livestream", {
                validateTrigger: ["onBlur"],
                valuePropName: "checked",
                initialValue: !!definition.livestream,
              })(<Input type="checkbox" />)}
            </Form.Item>

            <Form.Item label="Countdown">
              {form.getFieldDecorator("countdown", {
                initialValue: definition.countdown,
              })(<InputNumber />)}
            </Form.Item>

            <Form.Item label="Objects">
              <Collapse>
                {get(definition, "objects", []).map((object, objectIndex) => (
                  <Collapse.Panel
                    key={objectIndex}
                    header={
                      <span>
                        {object.name} ({object.id})
                      </span>
                    }
                    forceRender={true}
                    extra={
                      <Popconfirm
                        onClick={(e) => e.stopPropagation()}
                        onConfirm={(e) => {
                          e.stopPropagation();
                          this.handleRemoveObjectRequested(objectIndex);
                        }}
                        onCancel={(e) => e.stopPropagation()}
                        title="Do you really want to delete this event?"
                        cancelText="No"
                        okText="Yes"
                      >
                        <Button type="danger" size="small">
                          <Icon type="delete" />
                        </Button>
                      </Popconfirm>
                    }
                  >
                    <Form.Item
                      label="ID"
                      labelCol={{ span: 3 }}
                      wrapperCol={{ span: 21 }}
                    >
                      {form.getFieldDecorator(`objects[${objectIndex}].id`, {
                        rules: [{ required: true, message: "ID is required" }],
                        initialValue: get(object, "id"),
                      })(<Input size="small" />)}
                    </Form.Item>
                    <Form.Item
                      label="Object type"
                      labelCol={{ span: 3 }}
                      wrapperCol={{ span: 21 }}
                    >
                      {form.getFieldDecorator(`objects[${objectIndex}].type`, {
                        rules: [
                          {
                            required: true,
                            message: "Provide an object type",
                          },
                        ],
                        initialValue: get(object, "type"),
                      })(
                        <Select placeholder="Select a type">
                          <Select.Option value={null}>---</Select.Option>
                          <Select.Option value="html">HTML</Select.Option>
                          <Select.Option value="audio">Audio</Select.Option>
                          <Select.Option value="video">Video</Select.Option>
                          <Select.Option value="pdf">PDF</Select.Option>
                        </Select>
                      )}
                    </Form.Item>
                    <Form.Item
                      label="Name"
                      labelCol={{ span: 3 }}
                      wrapperCol={{ span: 21 }}
                    >
                      {form.getFieldDecorator(`objects[${objectIndex}].name`, {
                        rules: [
                          { required: true, message: "Name is required" },
                        ],
                        initialValue: get(object, "name"),
                      })(<Input size="small" />)}
                    </Form.Item>
                    <Form.Item
                      label="Description"
                      labelCol={{ span: 3 }}
                      wrapperCol={{ span: 21 }}
                    >
                      {form.getFieldDecorator(
                        `objects[${objectIndex}].description`,
                        {
                          initialValue: get(object, "description"),
                        }
                      )(<Input size="small" />)}
                    </Form.Item>
                    <Form.Item
                      label="Content"
                      labelCol={{ span: 3 }}
                      wrapperCol={{ span: 21 }}
                    >
                      {form.getFieldDecorator(
                        `objects[${objectIndex}].content`,
                        {
                          rules: [
                            { required: true, message: "Content is required" },
                          ],
                          initialValue: get(object, "content"),
                        }
                      )(<Input.TextArea size="small" />)}
                    </Form.Item>
                    <Collapse
                      defaultActiveKey={get(object, "metrics") ? 1 : undefined}
                    >
                      <Collapse.Panel
                        header="Object metrics"
                        forceRender={true}
                        key={1}
                      >
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={Object.entries(
                            get(object, "metrics", {})
                          ).map(([id, value]) => ({ id, value }))}
                          rowKey="id"
                          footer={() => (
                            <Button
                              onClick={() =>
                                this.handleAddObjectMetricRequested(objectIndex)
                              }
                              size="small"
                            >
                              <Icon type="plus" />
                              Add metric
                            </Button>
                          )}
                        >
                          <Table.Column title="Id" dataIndex="id" key="id" />
                          <Table.Column
                            title="Value"
                            dataIndex="value"
                            key="value"
                            render={(value, record, _index) =>
                              form.getFieldDecorator(
                                `objects[${objectIndex}].metrics.${record.id}`,
                                {
                                  initialValue: value,
                                }
                              )(<InputNumber size="small" />)
                            }
                          />
                          <Table.Column
                            title="Actions"
                            key="actions"
                            render={(_text, { id }, _index) => (
                              <Popconfirm
                                onConfirm={() =>
                                  this.handleRemoveObjectMetricRequested(
                                    objectIndex,
                                    id
                                  )
                                }
                                title="Do you really want to delete this metric?"
                                cancelText="No"
                                okText="Yes"
                              >
                                <Button type="danger" size="small">
                                  <Icon type="delete" />
                                </Button>
                              </Popconfirm>
                            )}
                          />
                        </Table>
                      </Collapse.Panel>
                    </Collapse>
                  </Collapse.Panel>
                ))}
              </Collapse>
              <Button onClick={this.handleAddObjectRequested} size="small">
                <Icon type="plus" />
                Add object
              </Button>
              <Modal
                visible={!!addObject}
                onCancel={this.handleAddObjectCancelled}
                title="Add an object"
                footer={null}
                centered={true}
                width={"80vw"}
              >
                {addObject && (
                  <AddObjectForm onSubmit={this.handleAddObjectSubmitted} />
                )}
              </Modal>
            </Form.Item>

            <Form.Item label="Metrics">
              <Table
                size="small"
                pagination={false}
                dataSource={get(definition, "metrics", [])}
                rowKey="id"
                footer={() => (
                  <Button onClick={this.handleAddMetricRequested} size="small">
                    <Icon type="plus" />
                    Add metric
                  </Button>
                )}
              >
                <Table.Column
                  title="Id"
                  dataIndex="id"
                  key="id"
                  render={(id, record, index) =>
                    form.getFieldDecorator(`metrics[${index}].id`, {
                      initialValue: id,
                    })(<Input size="small" />)
                  }
                />
                <Table.Column
                  title="Name"
                  dataIndex="name"
                  key="name"
                  render={(name, record, index) =>
                    form.getFieldDecorator(`metrics[${index}].name`, {
                      initialValue: name,
                    })(<Input size="small" />)
                  }
                />
                <Table.Column
                  title="Hidden"
                  dataIndex="hidden"
                  key="hidden"
                  render={(value, record, index) =>
                    form.getFieldDecorator(`metrics[${index}].hidden`, {
                      initialValue: !!value,
                      valuePropName: "checked",
                      validateTrigger: ["onBlur"],
                    })(<Input type="checkbox" />)
                  }
                />
                <Table.Column
                  title="Actions"
                  key="actions"
                  render={(_text, _record, index) => (
                    <Popconfirm
                      onConfirm={() => this.handleRemoveMetricRequested(index)}
                      title="Do you really want to delete this metric?"
                      cancelText="No"
                      okText="Yes"
                    >
                      <Button type="danger" size="small">
                        <Icon type="delete" />
                      </Button>
                    </Popconfirm>
                  )}
                />
              </Table>
            </Form.Item>

            <Form.Item label="Events" labelCol={null} wrapperCol={null}>
              {definition.events.length ? (
                <EventsList
                  form={form}
                  definition={definition}
                  events={definition.events}
                  eventsById={eventsById}
                  lastAddedEventId={lastAddedEventId}
                  droppableId="events"
                  getDraggableId={this.getDraggableId}
                  getEventFieldName={(event, fieldName) =>
                    `eventsById.event-${event.id}.${fieldName}`
                  }
                  getEventDroppableId={(event, name = "") =>
                    `event-droppable-${event.id}.${name}`
                  }
                  getEventDraggableId={(event, name = "") =>
                    `event-draggable-${event.id}.${name}`
                  }
                  onEventIdChangeRequested={this.handleEventIdChangeRequested}
                  onAddEventAfterRequested={this.handleAddEventAfterRequested}
                  onAddQuestionChoiceRequested={
                    this.handleAddQuestionChoiceRequested
                  }
                  onRemoveQuestionChoiceRequested={
                    this.handleRemoveQuestionChoiceRequested
                  }
                  onAddQuestionNextRuleRequested={
                    this.handleAddQuestionNextRuleRequested
                  }
                  onRemoveQuestionNextRuleRequested={
                    this.handleRemoveQuestionNextRuleRequested
                  }
                  onRemoveEventRequested={this.handleRemoveEventRequested}
                  onMediaSelectionRequested={onMediaSelectionRequested}
                  onAddQuestionMetricRequested={
                    this.handleAddQuestionMetricRequested
                  }
                  onRemoveQuestionMetricRequested={
                    this.handleRemoveQuestionMetricRequested
                  }
                />
              ) : (
                <p>No events yet</p>
              )}
              <Button onClick={this.handleAddEventRequested}>
                <Icon type="plus" />
                Add event
              </Button>
              <Modal
                visible={!!addEvent}
                onCancel={this.handleAddEventCancelled}
                title="Add an event"
                footer={null}
                centered={true}
                width={"80vw"}
              >
                {addEvent && (
                  <AddEventForm onSubmit={this.handleAddEventSubmitted} />
                )}
              </Modal>
            </Form.Item>
          </Form>
        </DragDropContext>
      );
    }
  }
);

const AddObjectForm = Form.create()(
  class WrappedForm extends React.Component {
    handleSubmit = (e) => {
      const { form, onSubmit } = this.props;
      e.preventDefault();
      form.validateFields((err, values) => {
        if (!err) {
          onSubmit(values);
        }
      });
    };

    render() {
      const {
        form: { getFieldDecorator },
      } = this.props;
      return (
        <Form onSubmit={this.handleSubmit} autoComplete="off" layout="vertical">
          <Form.Item label="Unique identifier">
            {getFieldDecorator("id", {
              rules: [{ required: true, message: "ID is required" }],
            })(<Input />)}
          </Form.Item>
          <Form.Item label="Type">
            {getFieldDecorator("type", {
              rules: [{ required: true, message: "Type is required" }],
              validateTrigger: ["onBlur", "onChange"],
            })(
              <Select placeholder="Select a type" size="small">
                <Select.Option value={null}>---</Select.Option>
                <Select.Option value="html">HTML</Select.Option>
                <Select.Option value="audio">Audio</Select.Option>
                <Select.Option value="video">Video</Select.Option>
                <Select.Option value="pdf">PDF</Select.Option>
              </Select>
            )}
          </Form.Item>
          <Form.Item label="Name">
            {getFieldDecorator("name", {
              rules: [{ required: true, message: "Name is required" }],
            })(<Input size="small" />)}
          </Form.Item>
          <Form.Item label="Content">
            {getFieldDecorator("content", {
              rules: [{ required: true, message: "Content is required" }],
            })(<Input.TextArea size="small" />)}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      );
    }
  }
);

const AddEventForm = Form.create()(
  class WrappedForm extends React.Component {
    handleSubmit = (e) => {
      const { form, onSubmit } = this.props;
      e.preventDefault();
      form.validateFields((err, values) => {
        if (!err) {
          onSubmit(values);
        }
      });
    };

    render() {
      const {
        form: { getFieldDecorator },
      } = this.props;
      return (
        <Form onSubmit={this.handleSubmit} autoComplete="off" layout="vertical">
          <Form.Item label="Unique identifier">
            {getFieldDecorator("id")(<Input />)}
          </Form.Item>
          <Form.Item label="Type">
            {getFieldDecorator("type", {
              rules: [{ required: true, message: "Type is required" }],
              validateTrigger: ["onBlur", "onChange"],
            })(
              <Select placeholder="Select a type">
                <Select.Option value={null}>---</Select.Option>
                <Select.Option value="intro">Intro</Select.Option>
                <Select.Option value="question">Question</Select.Option>
                <Select.Option value="leaderboard">Leaderboard</Select.Option>
              </Select>
            )}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
          </Form.Item>
        </Form>
      );
    }
  }
);

export default ScenarioEditorFormContainer;
